"use client";

import { notify, confirmDialog } from "@/components/ui/dialogs";
import PageHeader from "@/components/layout/PageHeader";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Eye, 
  Trash2, 
  FolderOpen,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  DollarSign,
  AlertCircle,
  Save,
  Users,
  Edit,
  CheckCircle} from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../components/ui/data-table";
import { get, put, del, post } from "../utils/service";
import Drawer from "@/components/ui/Drawer";

interface Project {
  id: string;
  lead_id: string | null;
  name: string;
  client: string;
  client_email: string;
  client_phone: string;
  type: string;
  value: string | number | null;
  current_stage: number;
  status: string;
  progress: string;
  location: string;
  project_name_approved: boolean;
  is_lead: boolean;
  sales_team: string;
  ops_team: string;
  mgmt_team: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  currency: string;
  first_name: string;
  last_name: string;
}

interface ProjectsResponse {
  success: boolean;
  message: string;
  data: Project[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

const STAGES = [
  { id: 1, name: "Lead Capture & Qualification", color: "bg-blue-100 text-blue-800" },
  { id: 2, name: "Discovery & Solution Mapping", color: "bg-purple-100 text-purple-800" },
  { id: 3, name: "Proposal, Commercials & Approval", color: "bg-pink-100 text-pink-800" },
  { id: 4, name: "Contracting & Kickoff", color: "bg-orange-100 text-orange-800" },
  { id: 5, name: "Delivery Execution & Adoption", color: "bg-green-100 text-green-800" },
  { id: 6, name: "Feedback, Optimization & Growth", color: "bg-cyan-100 text-cyan-800" }
];

const PROJECT_TYPES = [
  "app_development",
  "web_development",
  "mobile_app",
  "web_design",
  "ui_ux_design",
  "figma_design",
  "talent_placement",
  "software_consulting",
  "api_development",
  "database_design",
  "cloud_infrastructure",
  "devops",
  "qa_testing",
  "maintenance_support",
  "custom_software"
];

const STATUS_COLORS = {
  on_track: "bg-green-100 text-green-800",
  at_risk: "bg-yellow-100 text-yellow-800",
  completed: "bg-blue-100 text-blue-800",
  delayed: "bg-red-100 text-red-800"
};

const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);

const FloatingDrawer = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  position?: "bottom" | "right";
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  useEffect(() => {
    const checkDevice = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };
    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const drawerPosition = isMobile || isTablet ? "bottom" : "right";

  if (drawerPosition === "bottom") {
    return (
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        position="bottom"
        heightPercentage={80}
        showCloseButton={false}
      >
        <div className="h-full flex flex-col bg-black text-white">
          {children}
        </div>
      </Drawer>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 z-[99998]"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ 
              type: 'spring', 
              damping: 30, 
              stiffness: 300 
            }}
            className="fixed top-4 right-4 bottom-4 w-96 bg-black border border-gray-800 shadow-2xl rounded-2xl z-[99999] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="h-full flex flex-col bg-black text-white">
              {children}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const StageChangeModal = ({ 
  isOpen, 
  onClose, 
  currentStage, 
  newStage, 
  onConfirm 
}: { 
  isOpen: boolean;
  onClose: () => void;
  currentStage: number;
  newStage: number;
  onConfirm: () => void;
}) => {
  const getStageName = (stage: number) => {
    return STAGES.find(s => s.id === stage)?.name || "Unknown";
  };

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
      position="right"
    >
      <div className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xs font-bold text-white">Confirm Stage Change</h3>
            <p className="text-xs text-gray-400 mt-1">Are you sure you want to change the project stage?</p>
          </div>
        </div>

        <div className="bg-gray-900 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-400 mb-1">Current Stage</p>
              <p className="text-xs font-semibold text-white">{getStageName(currentStage)}</p>
            </div>
            <div className="text-gray-400 text-xs">→</div>
            <div>
              <p className="text-xs text-gray-400 mb-1">New Stage</p>
              <p className="text-xs font-semibold text-primary">{getStageName(newStage)}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors"
          >
            Confirm Change
          </button>
        </div>
      </div>
    </FloatingDrawer>
  );
};

const AddProjectForm = ({ 
  isOpen, 
  onClose,
  onSuccess 
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const [formData, setFormData] = useState({
    name: "",
    client: "",
    client_email: "",
    client_phone: "",
    type: "app_development",
    value: "",
    location: "",
    currency: "UGX"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await post("/projects", formData);
      if (response.success) {
        onSuccess();
        onClose();
        setFormData({
          name: "",
          client: "",
          client_email: "",
          client_phone: "",
          type: "app_development",
          value: "",
          location: "",
          currency: "UGX"
        });
      }
    } catch (error) {
      console.error("Error creating project:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Add New Project</h2>
          <p className="text-xs text-gray-400 mt-1">Create a new project</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Project Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Client Name *</label>
          <input
            type="text"
            required
            value={formData.client}
            onChange={(e) => handleChange("client", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Client Email *</label>
            <input
              type="email"
              required
              value={formData.client_email}
              onChange={(e) => handleChange("client_email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Client Phone *</label>
            <input
              type="tel"
              required
              value={formData.client_phone}
              onChange={(e) => handleChange("client_phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Project Type *</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange("type", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {PROJECT_TYPES.map(type => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Project Value *</label>
            <input
              type="number"
              required
              value={formData.value}
              onChange={(e) => handleChange("value", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="UGX">UGX</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !formData.name || !formData.client || !formData.client_email || !formData.client_phone || !formData.value}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Create Project
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const EditProjectForm = ({ 
  isOpen, 
  onClose,
  onSuccess,
  project
}: { 
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  project: Project | null;
}) => {
  const [formData, setFormData] = useState({
    name: "",
    client: "",
    client_email: "",
    client_phone: "",
    type: "app_development",
    value: "",
    location: "",
    currency: "UGX"
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name,
        client: project.client,
        client_email: project.client_email,
        client_phone: project.client_phone,
        type: project.type,
        value: project.value !== null && project.value !== undefined ? project.value.toString() : "",
        location: project.location || "",
        currency: project.currency
      });
    }
  }, [project]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;

    setIsSubmitting(true);

    try {
      const response = await put(`/projects/${project.id}`, formData);
      if (response.success) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating project:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (!project) return null;

  return (
    <FloatingDrawer
      isOpen={isOpen}
      onClose={onClose}
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Edit Project</h2>
          <p className="text-xs text-gray-400 mt-1">Update project details</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Project Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Client Name *</label>
          <input
            type="text"
            required
            value={formData.client}
            onChange={(e) => handleChange("client", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Client Email *</label>
            <input
              type="email"
              required
              value={formData.client_email}
              onChange={(e) => handleChange("client_email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Client Phone *</label>
            <input
              type="tel"
              required
              value={formData.client_phone}
              onChange={(e) => handleChange("client_phone", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Project Type *</label>
          <select
            value={formData.type}
            onChange={(e) => handleChange("type", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {PROJECT_TYPES.map(type => (
              <option key={type} value={type}>
                {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Project Value *</label>
            <input
              type="number"
              required
              value={formData.value}
              onChange={(e) => handleChange("value", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Currency</label>
            <select
              value={formData.currency}
              onChange={(e) => handleChange("currency", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="UGX">UGX</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Location</label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Updating...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Update Project
            </>
          )}
        </button>
        
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const ProjectDrawer = ({ 
  project, 
  isOpen, 
  onClose,
  onDelete,
  onEdit,
  onStageChange,
  onApproveName
}: { 
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onDelete: (projectId: string) => void;
  onEdit: () => void;
  onStageChange: (projectId: string, stage: number) => void;
  onApproveName: (projectId: string) => void;
}) => {
  const [currentView, setCurrentView] = useState<"details" | "stageConfirm">("details");
  const [selectedStage, setSelectedStage] = useState(1);

  useEffect(() => {
    if (isOpen) {
      setCurrentView("details");
      setSelectedStage(project?.current_stage || 1);
    }
  }, [isOpen, project]);

  if (!project) return null;

  const handleStageChange = (newStage: number) => {
    setSelectedStage(newStage);
    setCurrentView("stageConfirm");
  };

  const confirmStageChange = () => {
    onStageChange(project.id, selectedStage);
    setCurrentView("details");
    onClose();
  };

  const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
    if (amount === null || amount === undefined) {
      return `${currency} 0`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) {
      return `${currency} 0`;
    }
    if (numAmount >= 1000000000) {
      return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
    } else if (numAmount >= 1000000) {
      return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${numAmount.toLocaleString()}`;
  };

  const parseTeam = (teamString: string | null) => {
    if (!teamString) return [];
    try {
      return JSON.parse(teamString);
    } catch {
      return [];
    }
  };

  const renderDetailsView = () => (
    <>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Project Details</h2>
          <p className="text-xs text-gray-400 mt-1">{project.name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Stage</label>
            <select
              value={project.current_stage}
              onChange={(e) => handleStageChange(parseInt(e.target.value))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {STAGES.map(stage => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Status</label>
            <div className={`px-3 py-2 text-xs font-semibold rounded-lg ${STATUS_COLORS[project.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800'}`}>
              {project.status.replace('_', ' ').toUpperCase()}
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Progress</label>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-gray-800 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${project.progress}%` }}
              />
            </div>
            <span className="text-xs font-medium text-white">{project.progress}%</span>
          </div>
        </div>

        {!project.project_name_approved && (
          <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs font-semibold text-yellow-400">Project Name Pending Approval</p>
                <p className="text-xs text-yellow-300 mt-1">This project name requires approval before proceeding.</p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <FolderOpen className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Project Name</p>
              <p className="text-xs text-gray-400">{project.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Client</p>
              <p className="text-xs text-gray-400">{project.client}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Client Email</p>
              <p className="text-xs text-gray-400">{project.client_email}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Phone className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Client Phone</p>
              <p className="text-xs text-gray-400">{project.client_phone}</p>
            </div>
          </div>

          {project.location && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Location</p>
                <p className="text-xs text-gray-400">{project.location}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Project Value</p>
              <p className="text-xs text-gray-400">{formatCurrency(project.value, project.currency)}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Type</p>
              <p className="text-xs text-gray-400 capitalize">
                {project.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(project.created_at).toLocaleDateString()} by {project.first_name} {project.last_name}
              </p>
            </div>
          </div>
        </div>

        {parseTeam(project.sales_team).length > 0 && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block flex items-center gap-2">
              <Users className="w-4 h-4" />
              Sales Team
            </label>
            <div className="bg-gray-900 p-3 rounded-lg">
              {parseTeam(project.sales_team).map((member: string, idx: number) => (
                <p key={idx} className="text-xs text-gray-400">{member}</p>
              ))}
            </div>
          </div>
        )}

        {parseTeam(project.ops_team).length > 0 && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block flex items-center gap-2">
              <Users className="w-4 h-4" />
              Operations Team
            </label>
            <div className="bg-gray-900 p-3 rounded-lg">
              {parseTeam(project.ops_team).map((member: string, idx: number) => (
                <p key={idx} className="text-xs text-gray-400">{member}</p>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-800 space-y-2">
        {!project.project_name_approved && (
          <button
            onClick={() => onApproveName(project.id)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors"
          >
            <CheckCircle className="w-4 h-4" />
            Approve Project Name
          </button>
        )}
        
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={onEdit}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          
          <button
            onClick={async () => {
              if (await confirmDialog("Are you sure you want to delete this project?")) {
                onDelete(project.id);
              }
            }}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>
    </>
  );

  return (
    <>
      <FloatingDrawer
        isOpen={isOpen && currentView !== "stageConfirm"}
        onClose={onClose}
      >
        {renderDetailsView()}
      </FloatingDrawer>

      <StageChangeModal
        isOpen={currentView === "stageConfirm"}
        onClose={() => setCurrentView("details")}
        currentStage={project.current_stage}
        newStage={selectedStage}
        onConfirm={confirmStageChange}
      />
    </>
  );
};

function ActionsCell({ onView, onEdit, onDelete }: { project: Project; onView: () => void; onEdit: () => void; onDelete: () => void }) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu isOpen={isOpen} onClose={handleClose} position={position}>
          <TableDropdownItem onClick={() => { onView(); handleClose(); }}>
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onEdit(); handleClose(); }}>
            <Edit className="w-3 h-3" />
            Edit Project
          </TableDropdownItem>
          <TableDropdownItem onClick={() => { onDelete(); handleClose(); }} className="text-red-600 hover:bg-red-50">
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery] = useState("");
  const [statusFilter] = useState("all");
  const [typeFilter] = useState("all");
  const [stageFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [showProjectDrawer, setShowProjectDrawer] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showEditProject, setShowEditProject] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 5,
    total: 0,
    total_pages: 0,
  });

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const url =
        stageFilter !== "all"
          ? `/projects/stage/${stageFilter}?page=${pagination.page}&page_size=${pagination.per_page}`
          : `/projects?page=${pagination.page}&page_size=${pagination.per_page}`;

      const response = await get<ProjectsResponse>(url);
      if (response.success) {
        setProjects(response.data);
        setPagination((prev) => ({
          ...prev,
          total: response.pagination?.total ?? 0,
          total_pages: response.pagination?.total_pages ?? 0,
        }));
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, stageFilter]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Reset to page 1 when stage filter changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [stageFilter]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStageChange = async (projectId: string, stage: number) => {
    try {
      await put(`/projects/${projectId}/stage`, { stage });
      setProjects(prev => prev.map(project => 
        project.id === projectId ? { ...project, current_stage: stage } : project
      ));
      if (selectedProject?.id === projectId) {
        setSelectedProject(prev => prev ? { ...prev, current_stage: stage } : null);
      }
      notify("Project stage updated successfully");
    } catch (error) {
      console.error("Error updating stage:", error);
      notify("Error updating project stage");
    }
  };

  const handleApproveName = async (projectId: string) => {
    try {
      await post(`/projects/${projectId}/approve-name`, {});
      setProjects(prev => prev.map(project => 
        project.id === projectId ? { ...project, project_name_approved: true } : project
      ));
      if (selectedProject?.id === projectId) {
        setSelectedProject(prev => prev ? { ...prev, project_name_approved: true } : null);
      }
      notify("Project name approved successfully");
    } catch (error) {
      console.error("Error approving project name:", error);
      notify("Error approving project name");
    }
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected projects? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/projects/${r.id}`)));
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setProjects((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} projects deleted successfully`);
  };

  const handleDelete = async (projectId: string) => {
    if (!await confirmDialog("Are you sure you want to delete this project?")) return;

    try {
      await del(`/projects/${projectId}`);
      setProjects(prev => prev.filter(project => project.id !== projectId));
      if (selectedProject?.id === projectId) {
        setShowProjectDrawer(false);
        setSelectedProject(null);
      }
      notify("Project deleted successfully");
    } catch (error) {
      console.error("Error deleting project:", error);
      notify("Error deleting project");
    }
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client_email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === "all" || project.status === statusFilter;
    const matchesType = typeFilter === "all" || project.type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const formatCurrency = (amount: number | string | null, currency: string = "USD") => {
    if (amount === null || amount === undefined) {
      return `${currency} 0`;
    }
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numAmount)) {
      return `${currency} 0`;
    }
    if (numAmount >= 1000000000) {
      return `${currency} ${(numAmount / 1000000000).toFixed(1)}B`;
    } else if (numAmount >= 1000000) {
      return `${currency} ${(numAmount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${numAmount.toLocaleString()}`;
  };

  const handleViewProject = (project: Project) => {
    setSelectedProject(project);
    setShowProjectDrawer(true);
  };

  const handleEditProject = (project: Project) => {
    setSelectedProject(project);
    setShowEditProject(true);
  };

  const handleAddSuccess = () => {
    fetchProjects();
    notify("Project created successfully");
  };

  const handleEditSuccess = () => {
    fetchProjects();
    notify("Project updated successfully");
  };

  const columns: ColumnDef<Project>[] = [
    {
      accessorKey: "name",
      header: "Project Name",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">{row.original.name}</p>
          <p className="text-xs text-gray-600">{row.original.client}</p>
        </div>
      ),
    },
    // {
    //   accessorKey: "type",
    //   header: "Type",
    //   cell: ({ row }) => (
    //     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
    //       {row.original.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "value",
      header: "Value",
      cell: ({ row }) => (
        <p className="text-xs font-semibold text-gray-900">
          {formatCurrency(row.original.value, row.original.currency)}
        </p>
      ),
    },
    // {
    //   accessorKey: "status",
    //   header: "Status",
    //   cell: ({ row }) => (
    //     <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
    //       STATUS_COLORS[row.original.status as keyof typeof STATUS_COLORS] || 'bg-gray-100 text-gray-800'
    //     }`}>
    //       {row.original.status.replace('_', ' ').toUpperCase()}
    //     </span>
    //   ),
    // },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <div>
          <p className="text-xs text-gray-600">
            {new Date(row.original.created_at).toLocaleDateString()}
          </p>
          <p className="text-xs text-gray-500">
            by {row.original.first_name} {row.original.last_name}
          </p>
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          project={row.original}
          onView={() => handleViewProject(row.original)}
          onEdit={() => handleEditProject(row.original)}
          onDelete={() => handleDelete(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Project Portfolio" description="Monitor project progress and delivery timelines">
          {/* <button
              onClick={() => setShowAddProject(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
            >
              Add Project
            </button> */}
        </PageHeader>

        {/* Table Section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredProjects} onDeleteSelected={handleBatchDelete}>
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup key={headerGroup.id} headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead key={header.id} header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow key={row.id} row={row} className="hover:bg-gray-50 transition-colors border-b border-gray-100">
                      {({ cell }) => <DataTableCell key={cell.id} cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredProjects.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">No projects found</h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" || typeFilter !== "all" || stageFilter !== "all" 
                      ? "Adjust your search criteria or filters to find relevant projects."
                      : "Begin building your project portfolio by creating your first project."
                    }
                  </p>
                  {!searchQuery && statusFilter === "all" && typeFilter === "all" && stageFilter === "all" && (
                    <button
                      type="button"
                      onClick={() => setShowAddProject(true)}
                      className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                    >
                      Create First Project
                    </button>
                  )}
                </div>
              )}

              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                  <p className="text-xs text-gray-600">
                    Showing{" "}
                    {((pagination.page - 1) * pagination.per_page) + 1}–
                    {Math.min(pagination.page * pagination.per_page, pagination.total)}{" "}
                    of {pagination.total} projects
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      aria-label="Previous page"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <span className="px-2 py-1 text-xs text-gray-600">
                      Page {pagination.page} of {pagination.total_pages}
                    </span>
                    <button
                      type="button"
                      aria-label="Next page"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page === pagination.total_pages}
                      className="p-1.5 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <ProjectDrawer
        project={selectedProject}
        isOpen={showProjectDrawer}
        onClose={() => {
          setShowProjectDrawer(false);
          setSelectedProject(null);
        }}
        onDelete={handleDelete}
        onEdit={() => {
          setShowProjectDrawer(false);
          setShowEditProject(true);
        }}
        onStageChange={handleStageChange}
        onApproveName={handleApproveName}
      />

      <AddProjectForm
        isOpen={showAddProject}
        onClose={() => setShowAddProject(false)}
        onSuccess={handleAddSuccess}
      />

      <EditProjectForm
        isOpen={showEditProject}
        onClose={() => {
          setShowEditProject(false);
          setSelectedProject(null);
        }}
        onSuccess={handleEditSuccess}
        project={selectedProject}
      />
    </div>
  );
}

export default ProjectsPage;